import { DEFAULT_TIMEZONE } from "@sagot/constants";
import { formatMoney, useTranslation } from "@sagot/i18n";
import { useMemo } from "react";
import { useWorkspaceDetails } from "../../../session/workspace-context";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export type AgeUnit = "minute" | "hour" | "day";

/** Compact age parts: `null` = just now, else value + unit (rendered with Intl narrow units). */
export function ageParts(iso: string, now: number): { value: number; unit: AgeUnit } | null | "date" {
  const diff = Math.max(now - new Date(iso).getTime(), 0);
  if (diff < MINUTE) return null;
  if (diff < HOUR) return { value: Math.floor(diff / MINUTE), unit: "minute" };
  if (diff < DAY) return { value: Math.floor(diff / HOUR), unit: "hour" };
  if (diff < 7 * DAY) return { value: Math.floor(diff / DAY), unit: "day" };
  return "date";
}

export interface Formatters {
  locale: string;
  timeZone: string;
  number: (value: number) => string;
  /** `money("7500.00", "PHP")` → `₱7,500`; minor units only when present (DEC-042). */
  money: (amount: string | number | null | undefined, currency: string) => string;
  /** Always shows minor units, for invoice tables (`$146.67`, `₱7,500.00`). */
  moneyExact: (amount: string | number | null | undefined, currency: string) => string;
  /** "Oct 14, 2026" */
  date: (iso: string | null | undefined) => string;
  /** "Oct 14" */
  shortDate: (iso: string | null | undefined) => string;
  /** "Oct 14, 2:08 PM" */
  dateTime: (iso: string | null | undefined) => string;
  /** "2:08 PM" */
  time: (iso: string | null | undefined) => string;
  /** "3 min ago" / "2 hours ago" / "Oct 10" */
  relative: (iso: string | null | undefined) => string;
  /** "2m", "1h", "3d" */
  age: (iso: string | null | undefined) => string;
}

/** Locale + workspace timezone aware formatters (rule 15: dates in workspace TZ; money per currency). */
export function useFormatters(): Formatters {
  const { i18n } = useTranslation();
  const details = useWorkspaceDetails();
  const timeZone = details.data?.timezone ?? DEFAULT_TIMEZONE;
  const language = i18n.language;
  const locale = language === "fil" ? "fil-PH" : "en-PH";
  return useMemo(() => {
    const make = (options: Intl.DateTimeFormatOptions) => {
      const format = new Intl.DateTimeFormat(locale, { timeZone, ...options });
      return (iso: string | null | undefined) => (iso ? format.format(new Date(iso)) : "—");
    };
    const shortDate = make({ month: "short", day: "numeric" });
    const relativeFormat = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    const relative = (iso: string | null | undefined) => {
      if (!iso) return "—";
      const diff = new Date(iso).getTime() - Date.now();
      const abs = Math.abs(diff);
      if (abs < MINUTE) return relativeFormat.format(0, "second");
      if (abs < HOUR) return relativeFormat.format(Math.round(diff / MINUTE), "minute");
      if (abs < DAY) return relativeFormat.format(Math.round(diff / HOUR), "hour");
      if (abs < 7 * DAY) return relativeFormat.format(Math.round(diff / DAY), "day");
      return shortDate(iso);
    };
    return {
      locale,
      timeZone,
      number: (value: number) => value.toLocaleString(locale),
      money: (amount, currency) => formatMoney(amount, currency, language),
      moneyExact: (amount, currency) => formatMoney(amount, currency, language, { exact: true }),
      date: make({ month: "short", day: "numeric", year: "numeric" }),
      shortDate,
      dateTime: make({ month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }),
      time: make({ hour: "numeric", minute: "2-digit" }),
      relative,
      age: (iso) => {
        if (!iso) return "—";
        const parts = ageParts(iso, Date.now());
        if (parts === null) return relativeFormat.format(0, "second");
        if (parts === "date") return shortDate(iso);
        return new Intl.NumberFormat(locale, { style: "unit", unit: parts.unit, unitDisplay: "narrow" }).format(parts.value);
      },
    };
  }, [language, locale, timeZone]);
}
